/* Responsibility: Set URL in browser based on filter states  */

import fn from '../lib/fn.js';
import preconditions from '../lib/preconditions.js';
import url from '../lib/urlQuery.js';

const T = FINN.travel;

const checkPreconditions = preconditions('urlState', {
    eventHub: 'eventHub',
    browserWindow: 'object',
    history: 'object',
});

const eq = function (filter1, filter2) {
    return filter1.filterId === filter2.filterId && filter1.itemId === filter2.itemId;
};

const notEqualArrays = function (arr1, arr2) {
    return arr2.length !== arr1.length || fn.intersection(arr1, arr2).length !== arr1.length;
};

const filterId = function (id) {
    if (typeof id === 'string') {
        id = { filterId: id };
    }
    return id;
};

const createUrlParams = function (key, values) {
    return fn.map((value) => `${key}=${value}`, values);
};

const translateKey = function (inKey) {
    // Fix for supporting urls used in newsletter july 2014
    if (inKey === 'destinasjon') {
        return 'til';
    }
    return inKey;
};

export default function setupUrlState(params) {
    checkPreconditions(params);

    const currentState = [];
    let currentUrlParams = [];
    let stateJustPushed = false;
    let currentlyloading = true;
    let currentlyNotifyingFilters = false;
    const extraParams = ['WT.mc_id', 'enable_features'];

    T.urlQueryParams = {};

    const removeFromStateIfPresent = function (remove) {
        let x;
        let modified = false;
        for (x = 0; x < currentState.length; x += 1) {
            if (eq(currentState[x], filterId(remove))) {
                currentState.splice(x, 1);
                modified = true;
            }
        }
        return modified;
    };

    const setState = function () {
        const queryParts = [];
        let urn;

        fn.forEach(currentState, (param) => {
            queryParts.push(`${param.urlKey}=${param.urlValue}`);
        });

        // Temporary fix for keeping newsletter tracking
        fn.forEach(extraParams, (extraParam) => {
            if (T.urlQueryParams[extraParam]) {
                queryParts.push(`${extraParam}=${T.urlQueryParams[extraParam][0]}`);
            }
        });

        if (notEqualArrays(queryParts, currentUrlParams) && !currentlyNotifyingFilters && !currentlyloading) {
            stateJustPushed = true;
            urn = queryParts.length ? `?${queryParts.join('&')}` : '';
            params.history.replaceState(null, params.browserWindow.document.title, params.history.getBaseUrl() + urn, false);
        }
    };

    const setStateDesktop = () => {
        if (T.device !== 'mobile') {
            setState();
        }
    };

    const addFilter = function (data) {
        removeFromStateIfPresent(data);
        currentState.push(data);
        setStateDesktop();
    };

    const removeFilter = function (data) {
        removeFromStateIfPresent(data);
        setStateDesktop();
    };

    const changedFilters = function (data) {
        if (data.removed) {
            fn.forEach(data.removed, (filter) => {
                removeFromStateIfPresent(filter);
            });
        }
        if (data.added) {
            fn.forEach(data.added, (added) => {
                removeFromStateIfPresent(added);
                currentState.push(added);
            });
        }
        setStateDesktop();
    };

    const loadUrlQueryParams = function () {
        let key;
        const windowUrl = url(params.browserWindow.location.href);
        const query = windowUrl.queryParameters();
        for (key in T.urlQueryParams) {
            if (Object.prototype.hasOwnProperty.call(T.urlQueryParams, key)) {
                delete T.urlQueryParams[key];
            }
        }

        currentUrlParams = [];
        for (key in query) {
            if (Object.prototype.hasOwnProperty.call(query, key) && !fn.isFunction(query[key])) {
                const setKey = translateKey(key);
                if (fn.isList(query[key])) {
                    T.urlQueryParams[setKey] = query[key];
                } else {
                    T.urlQueryParams[setKey] = [query[key]];
                }
                currentUrlParams = currentUrlParams.concat(createUrlParams(setKey, T.urlQueryParams[setKey]));
            }
        }
    };

    loadUrlQueryParams();
    params.eventHub.subscribe('polling-complete', () => {
        currentlyloading = false;
    });
    params.eventHub.subscribe('filters:added', addFilter);
    params.eventHub.subscribe('filters:removed', removeFilter);
    params.eventHub.subscribe('filters:changed', changedFilters);
    params.eventHub.subscribe('filters:closed', setState);

    params.history.Adapter.bind(params.browserWindow, 'statechange', () => {
        loadUrlQueryParams();
        if (stateJustPushed) {
            stateJustPushed = false;
        } else {
            currentlyNotifyingFilters = true;
            params.eventHub.publish('urlQueryParams:changed');
            currentlyNotifyingFilters = false;
            params.eventHub.publish('resultViewCriteria:changed');
        }
    });
}
